/**
 * Form Designer command bar — the primary command interface.
 *
 * FORM / CARD / CONTROL actions live here, with icons and labels, so they
 * are never hidden inside canvas-only affordances. Enablement is driven by
 * the explicit selection: Delete Card only when a card is selected, Delete
 * Control only when a control is selected. Add Card on the empty canvas
 * calls the same `onAddCard` this bar uses.
 */
import {
  FilePlus2,
  Save,
  SquarePlus,
  TextCursorInput,
  Trash2,
} from "lucide-react";
import { FD } from "@/constants/testIds";
import { commandAvailability } from "@/lib/formDesignerCommands";

export default function FormDesignerCommandBar({
  form,
  selection,
  readOnly = false,
  dirty = false,
  busy = false,
  onNewForm,
  onDeleteForm,
  onAddCard,
  onDeleteCard,
  onAddControl,
  onDeleteControl,
  onSave,
}) {
  const commands = commandAvailability({ form, selection, readOnly, dirty });

  return (
    <div className="efd__command-bar" data-testid={FD.commandBar} role="toolbar" aria-label="Form Designer commands">
      <div className="efd__command-group" role="group" aria-label="Form">
        <button
          type="button"
          className="efd__cmd"
          data-testid={FD.newFormBtn}
          disabled={!commands.canNewForm || busy}
          onClick={onNewForm}
        >
          <FilePlus2 className="efd__btn-icon" aria-hidden="true" />
          New Form
        </button>
        <button
          type="button"
          className="efd__cmd efd__cmd--danger"
          data-testid={FD.deleteFormBtn}
          disabled={!commands.canDeleteForm || busy}
          onClick={onDeleteForm}
        >
          <Trash2 className="efd__btn-icon" aria-hidden="true" />
          Delete Form
        </button>
      </div>

      <div className="efd__command-group" role="group" aria-label="Card">
        <button
          type="button"
          className="efd__cmd"
          data-testid={FD.addCardBtn}
          disabled={!commands.canAddCard || busy}
          onClick={onAddCard}
        >
          <SquarePlus className="efd__btn-icon" aria-hidden="true" />
          Add Card
        </button>
        <button
          type="button"
          className="efd__cmd efd__cmd--danger"
          data-testid={FD.deleteCardBtn}
          disabled={!commands.canDeleteCard || busy}
          onClick={onDeleteCard}
        >
          <Trash2 className="efd__btn-icon" aria-hidden="true" />
          Delete Card
        </button>
      </div>

      <div className="efd__command-group" role="group" aria-label="Control">
        <button
          type="button"
          className="efd__cmd"
          data-testid={FD.addControlBtn}
          disabled={!commands.canAddControl || busy}
          onClick={onAddControl}
        >
          <TextCursorInput className="efd__btn-icon" aria-hidden="true" />
          Add Control
        </button>
        <button
          type="button"
          className="efd__cmd efd__cmd--danger"
          data-testid={FD.deleteControlBtn}
          disabled={!commands.canDeleteControl || busy}
          onClick={onDeleteControl}
        >
          <Trash2 className="efd__btn-icon" aria-hidden="true" />
          Delete Control
        </button>
      </div>

      <div className="efd__command-group efd__command-group--end" role="group" aria-label="Save">
        <button
          type="button"
          className="efd__btn efd__btn--primary"
          data-testid={FD.saveBtn}
          disabled={!commands.canSave || busy}
          onClick={onSave}
        >
          <Save className="efd__btn-icon" aria-hidden="true" />
          Save
        </button>
      </div>
    </div>
  );
}
